import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { emailValidator, noWhitespaceValidator } from '@core/common-legacy';
import {
  existingAppMemberValidator,
  existingTellentMemberValidator,
  TellentMembersHttpClient,
} from '@core/tellent-data';
import { InviteMethod, LoginType, MemberFormField } from '@core/tellent-types';
import { unicodeTextValidator } from '@core/tellent-util';

import { TellentAccessService } from '../tellent-access.service';

interface MemberDetailsFormControls {
  email: FormControl;
  firstName: FormControl;
  lastName: FormControl;
  login: FormControl;
}

@Injectable()
export class MemberDetailsFormService {
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly tellentMembersHttpClient = inject(TellentMembersHttpClient);
  private readonly formBuilder = inject(FormBuilder);

  public createMemberFormGroup(
    inviteMethod: InviteMethod,
    loginType?: LoginType,
  ): FormGroup<MemberFormField<{}>> {
    const memberFormGroup = this.formBuilder.group({
      email: this.formBuilder.control({ value: '', disabled: true }, { updateOn: 'blur' }),
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      login: [{ value: '', disabled: true }],
    });

    this.updateMemberGroup(memberFormGroup, inviteMethod, loginType);

    return memberFormGroup;
  }

  public updateMemberGroup(group: any, inviteMethod: string, loginType?: string): void {
    const email: FormControl = group.get('email');
    const firstName: FormControl = group.get('firstName');
    const lastName: FormControl = group.get('lastName');
    const login: FormControl = group.get('login');

    const controls: MemberDetailsFormControls = { email, firstName, lastName, login };

    this.resetFormControls(controls);

    switch (inviteMethod) {
      case InviteMethod.Email:
        this.resolveEmailFormState(controls);
        break;

      case InviteMethod.Login:
        email.reset('');
        switch (loginType) {
          case LoginType.Auto:
            this.resolveAutoLoginFormState(controls);
            break;

          case LoginType.Custom:
            this.resolveCustomLoginFormState(controls);
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }

    group.updateValueAndValidity();
  }

  private resolveEmailAsyncValidator() {
    return this.tellentAccessService.canManageTellentUsers()
      ? existingTellentMemberValidator(this.tellentMembersHttpClient)
      : existingAppMemberValidator(this.tellentMembersHttpClient);
  }

  private resetFormControls(controls: MemberDetailsFormControls): void {
    const { email, firstName, lastName, login } = controls;

    email.disable();
    email.clearValidators();
    firstName.disable();
    firstName.clearValidators();
    lastName.disable();
    lastName.clearValidators();
    login.disable();
    login.clearValidators();
  }

  private resolveEmailFormState(controls: MemberDetailsFormControls): void {
    const { email, firstName, lastName, login } = controls;

    login.reset('');
    email.enable();
    email.setValidators([Validators.required, emailValidator]);
    email.setAsyncValidators(this.resolveEmailAsyncValidator());
    email.updateValueAndValidity();
    firstName.enable();
    firstName.setValidators([noWhitespaceValidator, Validators.required]);
    lastName.updateValueAndValidity();
    lastName.enable();
    lastName.setValidators([noWhitespaceValidator, Validators.required]);
    lastName.updateValueAndValidity();
  }

  private resolveAutoLoginFormState(controls: MemberDetailsFormControls): void {
    const { firstName, lastName } = controls;

    firstName.enable();
    firstName.setValidators([Validators.required, noWhitespaceValidator, unicodeTextValidator]);
    firstName.updateValueAndValidity();
    lastName.enable();
    lastName.setValidators([Validators.required, noWhitespaceValidator, unicodeTextValidator]);
    lastName.updateValueAndValidity();
  }

  private resolveCustomLoginFormState(controls: MemberDetailsFormControls): void {
    const { firstName, lastName, login } = controls;

    firstName.enable();
    firstName.setValidators([noWhitespaceValidator, Validators.required]);
    firstName.updateValueAndValidity();
    lastName.enable();
    lastName.setValidators([noWhitespaceValidator, Validators.required]);
    lastName.updateValueAndValidity();
    login.enable();
    login.setValidators([Validators.required, noWhitespaceValidator, unicodeTextValidator]);
  }
}
